import { motion } from 'framer-motion';
import { Monitor, Projector, GraduationCap } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  viewport: { once: true, margin: '-100px' },
};

const EducationPage = () => {
  const solutions = [
    {
      icon: Monitor,
      title: 'Interactive Flat Panels',
      description:
        'Transform traditional classrooms into interactive learning environments with Samsung and Xerox display solutions.',
      features: ['4K Ultra HD Resolution', 'Multi-touch Capability', 'Built-in Educational Apps'],
    },
    {
      icon: Projector,
      title: 'Smart Classroom Setup',
      description:
        'Complete classroom technology packages including projectors, sound systems, and document cameras.',
      features: ['Wireless Connectivity', 'Cloud Integration', 'Remote Management'],
    },
    {
      icon: GraduationCap,
      title: 'Digital Learning Tools',
      description:
        'Advanced scanning and printing solutions for administrative efficiency and student services.',
      features: ['High-Speed Document Processing', 'Secure Print Release', 'Mobile Printing'],
    },
  ];

  return (
    <div data-testid="education-page" className="pt-32 pb-24 bg-[#F2F0E9]">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div {...fadeInUp} className="text-center mb-24">
          <h1 className="hero-title text-[#1A1A1A] mb-6 accent-yellow">
            Empowering Education
          </h1>
          <p className="text-lg md:text-xl text-[#595959] max-w-3xl mx-auto leading-relaxed">
            Specialized solutions for schools, colleges, and educational institutions. Technology that inspires learning.
          </p>
        </motion.div>

        {/* Hero Image */}
        <motion.div
          {...fadeInUp}
          data-testid="education-hero-image"
          className="mb-32 aspect-[21/9] overflow-hidden bg-white"
        >
          <img
            loading="lazy"
            decoding="async"
            src={process.env.PUBLIC_URL + '/images/hero_bg.webp'}
            alt="Education Technology"
            width="1200"
            height="514"
            className="w-full h-full object-cover img-desaturate"
          />
        </motion.div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16 mb-32">
          {solutions.map((solution, index) => {
            const Icon = solution.icon;
            return (
              <motion.div
                key={index}
                data-testid={`education-solution-${index}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true }}
              >
                <div className="mb-6">
                  <Icon size={40} className="text-[#1A1A1A]" strokeWidth={1} />
                </div>
                <h3 className="serif text-2xl text-[#1A1A1A] mb-4">{solution.title}</h3>
                <p className="text-base text-[#595959] leading-relaxed mb-6">
                  {solution.description}
                </p>
                <ul className="space-y-2">
                  {solution.features.map((feature, idx) => (
                    <li
                      key={idx}
                      className="text-sm text-[#595959] flex items-center gap-2"
                    >
                      <span className="w-1 h-1 bg-[#FFF200] rounded-full" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        {/* Testimonial Section */}
        <motion.div
          {...fadeInUp}
          data-testid="education-testimonial"
          className="bg-[#EBE8E1] py-20 px-6 md:px-12 text-center"
        >
          <blockquote className="max-w-3xl mx-auto">
            <p className="serif text-2xl md:text-3xl text-[#1A1A1A] italic mb-8 leading-relaxed">
              "Quality Services transformed our classrooms. The interactive displays have revolutionized how our teachers engage with students."
            </p>
            <footer className="text-sm uppercase tracking-widest text-[#595959]">
              Principal, St. Xavier's School
            </footer>
          </blockquote>
        </motion.div>

        {/* CTA */}
        <motion.div {...fadeInUp} className="text-center mt-24">
          <h2 className="serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
            Partner with Us
          </h2>
          <p className="text-lg text-[#595959] mb-8 max-w-2xl mx-auto">
            Let's discuss how we can enhance your institution's learning environment.
          </p>
          <button data-testid="schedule-demo-btn" className="luxury-button">
            Schedule a Demo
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default EducationPage;
